import { CREDENTIAL_FIELDS, SECRET_MASK } from '../models/credential-forms';
import {
  CompanyCredential,
  Invitation,
  LineOfBusiness,
  TreeMember,
} from '../models/domain.models';
import { INVITATION_VALID_DAYS } from '../models/rules';
import {
  CascadePreview,
  CredentialRow,
  CredentialsView,
  InvitationLookup,
  InvitationRow,
  MemberCard,
  TreeNodeRow,
} from '../models/views.models';
import { descendantsOf, effectiveStatus, holderOf, previousPeriod, subtreeOids } from './commission-math';
import { failure, Outcome } from './outcome';
import { addDays } from './rng';
import { COMPANY, MEMBER } from './seed-named';
import { StoreHost } from './store-host';

/** People already on the platform (in some other tree): inviting them associates instead of creating. */
const PLATFORM_PEOPLE = [
  { email: 'hernan.dupont@brokers.example', taxId: '20271112223', license: 'MAT-55120', name: 'Hernán Dupont', legalForm: 'natural' as const },
  { email: 'contacto@segurosdelsur.example', taxId: '30709988771', license: 'MAT-62001', name: 'Seguros del Sur SRL', legalForm: 'legal' as const },
];

const BLOCKED_USER = { memberOid: MEMBER.laura, companyOid: COMPANY.pampa, reason: 'Credenciales inválidas: la compañía respondió "usuario bloqueado"' };
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export interface Actor {
  oid: string | null;
  name: string;
}

function digits(value: string): string {
  return value.replace(/\D/g, '');
}

export class TreeEngine {
  constructor(private readonly host: StoreHost) {}

  private get db() {
    return this.host.db;
  }

  private me(): TreeMember | null {
    const oid = this.host.user()?.memberOid;
    return this.db.members.find(m => m.oid === oid) ?? null;
  }

  private isProducerSession(): boolean {
    return this.host.user()?.role === 'producer' && this.me() !== null;
  }

  private scoped(oid: string): TreeMember | null {
    const me = this.me();
    if (!me || !this.isProducerSession()) return null;
    return subtreeOids(this.db.members, me.oid).has(oid) ? this.db.members.find(m => m.oid === oid) ?? null : null;
  }

  private depthOf(oid: string, rootOid: string): number {
    let depth = 0;
    let current = this.db.members.find(m => m.oid === oid);
    while (current && current.oid !== rootOid && current.parentOid) {
      depth++;
      current = this.db.members.find(m => m.oid === current?.parentOid);
    }
    return depth;
  }

  defaultPeriod(): string {
    return previousPeriod(this.host.today());
  }

  private node(m: TreeMember, depth: number): TreeNodeRow {
    const effective = effectiveStatus(this.db.members, m.oid);
    const credentials = m.kind === 'producer' ? this.db.credentials.filter(c => c.memberOid === m.oid) : null;
    const owners = subtreeOids(this.db.members, m.oid);
    const period = this.defaultPeriod();
    const parent = m.parentOid ? this.db.members.find(x => x.oid === m.parentOid) : undefined;
    const by = effective.byOid && effective.byOid !== m.oid ? this.db.members.find(x => x.oid === effective.byOid) : undefined;
    return {
      oid: m.oid, name: m.name, kind: m.kind, legalForm: m.legalForm, license: m.license, status: m.status,
      effectiveStatus: effective.status, suspendedByName: by?.name ?? null, depth,
      parentOid: m.parentOid, parentName: parent?.name ?? null,
      childCount: this.db.members.filter(x => x.parentOid === m.oid).length,
      production: this.db.commissionLines.filter(l => l.period === period && owners.has(l.ownerOid))
        .reduce((sum, l) => sum + l.collectedPremium, 0),
      credentialsConfigured: credentials ? credentials.length : null,
      credentialsInvalid: credentials ? credentials.filter(c => c.validation === 'invalid').length : null,
      canEmit: m.canEmit, inOtherTrees: m.inOtherTrees,
    };
  }

  nodes(): TreeNodeRow[] {
    const me = this.me();
    if (!me || !this.isProducerSession()) return [];
    const out: TreeNodeRow[] = [];
    const walk = (m: TreeMember, depth: number) => {
      out.push(this.node(m, depth));
      this.db.members.filter(x => x.parentOid === m.oid).sort((a, b) => a.name.localeCompare(b.name))
        .forEach(child => walk(child, depth + 1));
    };
    walk(me, 0);
    return out;
  }

  // ── Card (P-81) ───────────────────────────────────────────────

  card(oid: string): Outcome<MemberCard> {
    const me = this.me();
    const member = this.scoped(oid);
    if (!me || !member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    const owners = subtreeOids(this.db.members, member.oid);
    const period = this.defaultPeriod();
    const holder = member.kind === 'seller' ? this.db.members.find(m => m.oid === holderOf(this.db.members, member.oid)) : undefined;
    const invitation = member.invitationOid ? this.db.invitations.find(i => i.oid === member.invitationOid) : undefined;
    const today = this.host.today();
    return {
      value: {
        node: this.node(member, this.depthOf(oid, me.oid)),
        member: structuredClone(member),
        children: this.db.members.filter(m => m.parentOid === oid).map(m => this.node(m, this.depthOf(m.oid, me.oid))),
        invitation: invitation ? this.invitationRow(invitation) : null,
        credentials: member.kind === 'producer' ? this.credentialRows(member) : [],
        holderName: holder?.name ?? null,
        canManage: oid !== me.oid,
        isSelf: oid === me.oid,
        period,
        periodProduction: this.db.commissionLines.filter(l => l.period === period && owners.has(l.ownerOid))
          .reduce((sum, l) => sum + l.collectedPremium, 0),
        policies: this.db.policies.filter(p => p.status === 'active' && owners.has(p.ownerOid)).length,
        split: this.db.splits
          .filter(s => s.parentOid === member.parentOid && s.memberOid === oid && s.validFrom <= today && (s.validTo === null || today <= s.validTo))
          .map(s => ({ lineOfBusiness: s.lineOfBusiness, sharePct: s.sharePct, validFrom: s.validFrom })),
      },
    };
  }

  editMember(oid: string, body: { name: string; email: string; phone: string; license?: string; taxId?: string }): Outcome<MemberCard> {
    const member = this.scoped(oid);
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    if (!body.name?.trim() || !EMAIL.test(body.email ?? '')) return failure(400, 'MEMBER_INVALID', 'Completá el nombre y un mail válido');
    if (member.kind === 'producer' && (!body.license?.trim() || digits(body.taxId ?? '').length !== 11)) {
      return failure(400, 'MEMBER_INVALID', 'Un productor necesita matrícula y un CUIT de 11 dígitos');
    }
    member.name = body.name.trim();
    member.email = body.email.trim();
    member.phone = body.phone?.trim() || null;
    if (member.kind === 'producer') {
      member.license = body.license?.trim() ?? member.license;
      member.taxId = body.taxId?.trim() ?? member.taxId;
    }
    return this.card(oid);
  }

  cascade(oid: string): Outcome<CascadePreview> {
    const member = this.scoped(oid);
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    return {
      value: {
        member: { oid: member.oid, name: member.name },
        affected: descendantsOf(this.db.members, oid).map(m => ({
          oid: m.oid, name: m.name, kind: m.kind, alreadySuspended: effectiveStatus(this.db.members, m.oid).status !== 'active',
        })),
      },
    };
  }

  /** Suspends `member`; the suspension reaches the subtree. Shared by the producer (P-81) and the platform admin (P-93). */
  suspendMember(member: TreeMember, actor: Actor): Outcome<TreeMember[]> {
    if (member.status !== 'active') return failure(409, 'MEMBER_NOT_ACTIVE', 'Ya está suspendido o dado de baja');
    const affected = descendantsOf(this.db.members, member.oid).filter(m => effectiveStatus(this.db.members, m.oid).status === 'active');
    member.status = 'suspended';
    member.statusHistory.push({
      date: this.host.today(), status: 'suspended', byOid: actor.oid,
      text: affected.length ? `Suspendido por ${actor.name}; alcanzó a ${affected.length} persona(s) de su subárbol` : `Suspendido por ${actor.name}`,
    });
    for (const m of [member, ...affected]) {
      this.host.notify(m.oid, 'system', 'Tu usuario fue suspendido',
        m.oid === member.oid ? `${actor.name} te suspendió en el árbol.` : `La suspensión de ${member.name} alcanza a tu rama del árbol.`, null, null);
    }
    return { value: affected };
  }

  reactivateMember(member: TreeMember, actor: Actor): Outcome<true> {
    if (member.status !== 'suspended') {
      const above = effectiveStatus(this.db.members, member.oid);
      return failure(409, 'MEMBER_NOT_SUSPENDED', member.status === 'baja' ? 'La baja no se revierte'
        : above.byOid && above.byOid !== member.oid
          ? `Está suspendido porque ${this.db.members.find(m => m.oid === above.byOid)?.name ?? 'un superior'} lo está: reactivá primero a esa persona`
          : 'No está suspendido');
    }
    const parentState = member.parentOid ? effectiveStatus(this.db.members, member.parentOid) : null;
    if (parentState && parentState.status !== 'active') {
      return failure(409, 'MEMBER_PARENT_SUSPENDED', 'Su productor padre está suspendido: reactivá primero a esa persona');
    }
    member.status = 'active';
    member.statusHistory.push({ date: this.host.today(), status: 'active', byOid: actor.oid, text: `Reactivado por ${actor.name}` });
    this.host.notify(member.oid, 'system', 'Tu usuario fue reactivado', `${actor.name} te reactivó en el árbol.`, null, null);
    return { value: true };
  }

  /** Exclusive to the platform admin: the node leaves the tree for good and its subtree goes with it. */
  bajaMember(member: TreeMember, actor: Actor): Outcome<TreeMember[]> {
    if (member.status === 'baja') return failure(409, 'MEMBER_ALREADY_BAJA', 'Ya está dado de baja');
    const affected = descendantsOf(this.db.members, member.oid).filter(m => effectiveStatus(this.db.members, m.oid).status !== 'baja');
    member.status = 'baja';
    member.statusHistory.push({
      date: this.host.today(), status: 'baja', byOid: actor.oid,
      text: affected.length ? `Baja dispuesta por ${actor.name}; alcanzó a ${affected.length} persona(s) de su subárbol` : `Baja dispuesta por ${actor.name}`,
    });
    for (const m of [member, ...affected]) {
      this.host.notify(m.oid, 'system', 'Tu usuario fue dado de baja',
        m.oid === member.oid ? 'La plataforma dio de baja tu usuario en este árbol.' : `La baja de ${member.name} alcanza a tu rama del árbol.`, null, null);
    }
    return { value: affected };
  }

  suspend(oid: string): Outcome<MemberCard> {
    const me = this.me();
    const member = this.scoped(oid);
    if (!me || !member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    if (member.oid === me.oid) return failure(409, 'MEMBER_SELF', 'No podés suspenderte a vos mismo');
    const done = this.suspendMember(member, { oid: me.oid, name: me.name });
    return 'error' in done ? done : this.card(oid);
  }

  reactivate(oid: string): Outcome<MemberCard> {
    const me = this.me();
    const member = this.scoped(oid);
    if (!me || !member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    const done = this.reactivateMember(member, { oid: me.oid, name: me.name });
    return 'error' in done ? done : this.card(oid);
  }

  setCanEmit(oid: string, canEmit: boolean): Outcome<MemberCard> {
    const member = this.scoped(oid);
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    if (member.kind !== 'seller') return failure(409, 'MEMBER_NOT_SELLER', 'Solo los vendedores tienen esta capacidad');
    if (member.canEmit !== canEmit) {
      member.canEmit = canEmit;
      this.host.notify(member.oid, 'system', canEmit ? 'Ahora podés emitir' : 'Ahora solo cotizás',
        canEmit ? 'Tu productor te habilitó a emitir con su matrícula.' : 'Tu productor te quitó la emisión: pasale las ventas para emitir.', null, null);
    }
    return this.card(oid);
  }

  saveOwnCodes(oid: string, codes: Record<string, string>): Outcome<MemberCard> {
    const member = this.scoped(oid);
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    if (member.kind !== 'seller') return failure(409, 'MEMBER_NOT_SELLER', 'Solo los vendedores tienen código propio');
    member.ownCodes = this.cleanCodes(codes);
    return this.card(oid);
  }

  private cleanCodes(codes: Record<string, string>): Record<string, string> {
    return Object.fromEntries(Object.entries(codes ?? {})
      .filter(([companyOid, code]) => code.trim() && this.db.companies.some(c => c.oid === companyOid))
      .map(([companyOid, code]) => [companyOid, code.trim()]));
  }

  createSeller(body: {
    firstName: string; lastName: string; email: string; phone: string; canEmit: boolean;
    parentOid?: string; codes?: Record<string, string>;
  }): Outcome<MemberCard> {
    const me = this.me();
    if (!me || !this.isProducerSession()) return failure(403, 'FORBIDDEN', 'Solo un productor crea vendedores');
    const parent = this.scoped(body.parentOid || me.oid);
    if (!parent || parent.kind !== 'producer') return failure(400, 'SELLER_PARENT_INVALID', 'El vendedor depende de un productor de tu árbol');
    if (effectiveStatus(this.db.members, parent.oid).status !== 'active') {
      return failure(409, 'SELLER_PARENT_SUSPENDED', 'No se pueden crear vendedores bajo un productor suspendido');
    }
    if (!body.firstName?.trim() || !body.lastName?.trim() || !EMAIL.test(body.email ?? '')) {
      return failure(400, 'SELLER_INVALID', 'Completá nombre, apellido y un mail válido');
    }
    if (this.db.members.some(m => m.treeOid === me.treeOid && m.email.toLowerCase() === body.email.trim().toLowerCase())) {
      return failure(409, 'SELLER_DUPLICATE', 'Ya hay una persona con ese mail en el árbol');
    }
    const seller: TreeMember = {
      oid: `mem-${this.host.nextSequence()}`, treeOid: me.treeOid, kind: 'seller', name: `${body.firstName.trim()} ${body.lastName.trim()}`,
      legalForm: 'natural', parentOid: parent.oid, status: 'active', canEmit: !!body.canEmit, license: null, taxId: null,
      email: body.email.trim(), phone: body.phone?.trim() || null, inOtherTrees: false,
      ownCodes: this.cleanCodes(body.codes ?? {}), invitationOid: null,
      statusHistory: [{ date: this.host.today(), status: 'active', byOid: me.oid, text: `Alta como vendedor por ${me.name}` }],
    };
    this.db.members.push(seller);
    return this.card(seller.oid);
  }

  // ── Invitations (P-82) ────────────────────────────────────────

  private invitationRow(i: Invitation): InvitationRow {
    return {
      oid: i.oid, email: i.email, license: i.license, taxId: i.taxId, name: i.name, legalForm: i.legalForm,
      existingPerson: i.existingPerson, sentDate: i.sentDate, expiryDate: i.expiryDate,
      status: i.accepted ? 'accepted' : i.expiryDate < this.host.today() ? 'expired' : 'pending',
      invitedByName: this.db.members.find(m => m.oid === i.invitedByOid)?.name ?? '—', memberOid: i.memberOid,
    };
  }

  invitations(): InvitationRow[] {
    const me = this.me();
    if (!me || !this.isProducerSession()) return [];
    const owners = subtreeOids(this.db.members, me.oid);
    return this.db.invitations.filter(i => owners.has(i.invitedByOid)).map(i => this.invitationRow(i))
      .sort((a, b) => b.sentDate.localeCompare(a.sentDate));
  }

  lookupInvitation(email: string, license: string, taxId: string): InvitationLookup {
    const treeOid = this.me()?.treeOid;
    const tax = digits(taxId);
    const mail = email.trim().toLowerCase();
    const lic = license.trim().toLowerCase();
    return {
      existingPerson: PLATFORM_PEOPLE.some(p => p.taxId === tax || p.email === mail || p.license.toLowerCase() === lic),
      alreadyInTree: this.db.members.some(m => m.treeOid === treeOid && (m.email.toLowerCase() === mail || (!!tax && digits(m.taxId ?? '') === tax)
        || (!!lic && (m.license ?? '').toLowerCase() === lic))),
    };
  }

  invite(body: { email: string; license: string; taxId: string; legalForm?: 'natural' | 'legal'; name?: string }): Outcome<InvitationRow> {
    const me = this.me();
    if (!me || !this.isProducerSession()) return failure(403, 'FORBIDDEN', 'Solo un productor invita');
    if (!EMAIL.test(body.email ?? '') || !body.license?.trim() || digits(body.taxId ?? '').length !== 11) {
      return failure(400, 'INVITATION_INVALID', 'Completá un mail válido, la matrícula y un CUIT de 11 dígitos');
    }
    const lookup = this.lookupInvitation(body.email, body.license, body.taxId);
    if (lookup.alreadyInTree) return failure(409, 'INVITATION_DUPLICATE', 'Esa persona ya está en tu árbol');
    const pending = this.invitations().find(i => i.status === 'pending' && i.email.toLowerCase() === body.email.trim().toLowerCase());
    if (pending) return failure(409, 'INVITATION_PENDING', 'Ya hay una invitación pendiente para ese mail: reenviala desde el listado');
    const known = PLATFORM_PEOPLE.find(p => p.taxId === digits(body.taxId) || p.email === body.email.trim().toLowerCase()
      || p.license.toLowerCase() === body.license.trim().toLowerCase());
    if (!known && (!body.name?.trim() || !body.legalForm)) {
      return failure(400, 'INVITATION_INCOMPLETE', 'La persona es nueva: indicá si es física o jurídica y su nombre o razón social');
    }
    const today = this.host.today();
    const invitation: Invitation = {
      oid: `inv-${String(this.db.invitations.length + 1).padStart(4, '0')}`, invitedByOid: me.oid,
      email: body.email.trim(), license: body.license.trim(), taxId: body.taxId.trim(), existingPerson: !!known,
      legalForm: known?.legalForm ?? body.legalForm ?? 'natural', name: known?.name ?? body.name?.trim() ?? '',
      sentDate: today, expiryDate: addDays(today, INVITATION_VALID_DAYS), accepted: false, memberOid: null,
    };
    this.db.invitations.push(invitation);
    return { value: this.invitationRow(invitation) };
  }

  resendInvitation(oid: string): Outcome<InvitationRow> {
    const invitation = this.invitationInScope(oid);
    if (!invitation) return failure(404, 'NOT_FOUND', 'Invitación inexistente');
    if (invitation.accepted) return failure(409, 'INVITATION_ACCEPTED', 'La invitación ya fue aceptada');
    invitation.sentDate = this.host.today();
    invitation.expiryDate = addDays(invitation.sentDate, INVITATION_VALID_DAYS);
    return { value: this.invitationRow(invitation) };
  }

  resendForMember(memberOid: string): Outcome<MemberCard> {
    const member = this.scoped(memberOid);
    const invitation = member?.invitationOid ? this.invitationInScope(member.invitationOid) : undefined;
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    if (!invitation) return failure(409, 'INVITATION_NONE', 'Esta persona no entró por invitación');
    const resent = this.resendInvitation(invitation.oid);
    return 'error' in resent ? resent : this.card(memberOid);
  }

  /** Demo shortcut: the invited person accepts, and joins the tree as a producer under whoever invited. */
  acceptInvitation(oid: string): Outcome<InvitationRow> {
    const invitation = this.invitationInScope(oid);
    if (!invitation) return failure(404, 'NOT_FOUND', 'Invitación inexistente');
    if (invitation.accepted) return failure(409, 'INVITATION_ACCEPTED', 'La invitación ya fue aceptada');
    const inviter = this.db.members.find(m => m.oid === invitation.invitedByOid);
    const member: TreeMember = {
      oid: `mem-${this.host.nextSequence()}`, treeOid: inviter?.treeOid ?? '', kind: 'producer', name: invitation.name, legalForm: invitation.legalForm,
      parentOid: invitation.invitedByOid, status: 'active', canEmit: true, license: invitation.license, taxId: invitation.taxId,
      email: invitation.email, phone: null, inOtherTrees: invitation.existingPerson, ownCodes: {}, invitationOid: invitation.oid,
      statusHistory: [{ date: this.host.today(), status: 'active', byOid: invitation.invitedByOid, text: 'Aceptó la invitación' }],
    };
    this.db.members.push(member);
    invitation.accepted = true;
    invitation.memberOid = member.oid;
    this.host.notify(invitation.invitedByOid, 'system', `${member.name} aceptó tu invitación`,
      'Ya figura en tu árbol: cargale las credenciales por compañía y el reparto de comisión.', null, null);
    return { value: this.invitationRow(invitation) };
  }

  private invitationInScope(oid: string): Invitation | undefined {
    const me = this.me();
    if (!me || !this.isProducerSession()) return undefined;
    const owners = subtreeOids(this.db.members, me.oid);
    return this.db.invitations.find(i => i.oid === oid && owners.has(i.invitedByOid));
  }

  // ── Credentials (P-83) ────────────────────────────────────────

  private canManageCredentials(target: TreeMember): boolean {
    const me = this.me();
    return !!me && target.kind === 'producer' && (target.parentOid === me.oid || (target.oid === me.oid && target.parentOid === null));
  }

  private credentialRows(producer: TreeMember): CredentialRow[] {
    return this.db.companies.map(company => {
      const credential = this.db.credentials.find(c => c.memberOid === producer.oid && c.companyOid === company.oid);
      const specs = CREDENTIAL_FIELDS[company.oid] ?? [];
      return {
        companyOid: company.oid, companyName: company.name, lines: company.lines, fieldSpecs: specs,
        configured: !!credential,
        fields: Object.fromEntries(specs.map(s => [s.key, s.secret ? (credential?.fields[s.key] ? SECRET_MASK : '') : credential?.fields[s.key] ?? ''])),
        producerCode: credential?.producerCode ?? '',
        sellerCodes: this.db.members
          .filter(m => m.kind === 'seller' && m.ownCodes[company.oid] && holderOf(this.db.members, m.oid) === producer.oid)
          .map(m => ({ sellerName: m.name, code: m.ownCodes[company.oid] })),
        validation: credential?.validation ?? null, validatedDate: credential?.validatedDate ?? null,
        validationMessage: credential?.validationMessage ?? null, enabledLines: credential?.enabledLines ?? [],
      };
    });
  }

  credentialsView(oid: string): Outcome<CredentialsView> {
    const member = this.scoped(oid);
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    const holder = member.kind === 'producer' ? member : this.db.members.find(m => m.oid === holderOf(this.db.members, member.oid));
    return {
      value: {
        member: structuredClone(member), holderOid: holder?.oid ?? null, holderName: holder?.name ?? null,
        editable: this.canManageCredentials(member),
        rows: member.kind === 'producer' ? this.credentialRows(member) : [],
      },
    };
  }

  private editableCredential(oid: string, companyOid: string): { member: TreeMember } | { error: ReturnType<typeof failure>['error'] } {
    const member = this.scoped(oid);
    if (!member) return failure(404, 'NOT_FOUND', 'No encontramos a esa persona en tu árbol');
    if (!this.canManageCredentials(member)) {
      return failure(403, 'CREDENTIALS_FORBIDDEN', 'Las credenciales las carga el productor padre de cada productor');
    }
    if (!this.db.companies.some(c => c.oid === companyOid)) return failure(404, 'NOT_FOUND', 'Compañía inexistente');
    return { member };
  }

  saveCredential(
    oid: string, companyOid: string, body: { fields: Record<string, string>; producerCode: string; enabledLines: LineOfBusiness[] },
  ): Outcome<CredentialRow> {
    const target = this.editableCredential(oid, companyOid);
    if ('error' in target) return target;
    const company = this.db.companies.find(c => c.oid === companyOid)!;
    const existing = this.db.credentials.find(c => c.memberOid === oid && c.companyOid === companyOid);
    const specs = CREDENTIAL_FIELDS[companyOid] ?? [];
    const fields: Record<string, string> = {};
    for (const spec of specs) {
      const typed = (body.fields?.[spec.key] ?? '').trim();
      fields[spec.key] = spec.secret && (typed === '' || typed === SECRET_MASK) ? existing?.fields[spec.key] ?? '' : typed;
    }
    if (specs.some(s => !fields[s.key]) || !body.producerCode?.trim()) {
      return failure(400, 'CREDENTIAL_INCOMPLETE', 'Completá los datos que pide la compañía y el código de productor');
    }
    const enabledLines = company.lines.filter(l => (body.enabledLines ?? company.lines).includes(l));
    if (existing) {
      existing.fields = fields;
      existing.producerCode = body.producerCode.trim();
      existing.enabledLines = enabledLines;
      existing.validation = 'unvalidated';
      existing.validatedDate = null;
      existing.validationMessage = null;
    } else {
      this.db.credentials.push({
        oid: `crd-${oid.slice(4)}-${companyOid.slice(4)}`, memberOid: oid, companyOid, fields,
        producerCode: body.producerCode.trim(), validation: 'unvalidated', validatedDate: null, validationMessage: null, enabledLines,
      });
    }
    return this.rowOf(target.member, companyOid);
  }

  setLines(oid: string, companyOid: string, enabledLines: LineOfBusiness[]): Outcome<CredentialRow> {
    const target = this.editableCredential(oid, companyOid);
    if ('error' in target) return target;
    const credential = this.db.credentials.find(c => c.memberOid === oid && c.companyOid === companyOid);
    if (!credential) return failure(409, 'CREDENTIAL_MISSING', 'Primero cargá las credenciales de esta compañía');
    const company = this.db.companies.find(c => c.oid === companyOid)!;
    credential.enabledLines = company.lines.filter(l => enabledLines.includes(l));
    return this.rowOf(target.member, companyOid);
  }

  validateCredential(oid: string, companyOid: string): Outcome<CredentialRow> {
    const target = this.editableCredential(oid, companyOid);
    if ('error' in target) return target;
    const credential = this.db.credentials.find(c => c.memberOid === oid && c.companyOid === companyOid);
    if (!credential) return failure(409, 'CREDENTIAL_MISSING', 'Primero cargá las credenciales de esta compañía');
    const secretRejected = (CREDENTIAL_FIELDS[companyOid] ?? []).some(s => s.secret && credential.fields[s.key] === 'invalida');
    credential.validatedDate = this.host.today();
    if (oid === BLOCKED_USER.memberOid && companyOid === BLOCKED_USER.companyOid) {
      credential.validation = 'invalid';
      credential.validationMessage = BLOCKED_USER.reason;
    } else if (secretRejected) {
      credential.validation = 'invalid';
      credential.validationMessage = 'La compañía rechazó las credenciales: usuario o clave incorrectos';
    } else {
      credential.validation = 'valid';
      credential.validationMessage = null;
    }
    return this.rowOf(target.member, companyOid);
  }

  deleteCredential(oid: string, companyOid: string): Outcome<CredentialRow> {
    const target = this.editableCredential(oid, companyOid);
    if ('error' in target) return target;
    const index = this.db.credentials.findIndex(c => c.memberOid === oid && c.companyOid === companyOid);
    if (index < 0) return failure(409, 'CREDENTIAL_MISSING', 'Esta compañía no tiene credenciales cargadas');
    this.db.credentials.splice(index, 1);
    return this.rowOf(target.member, companyOid);
  }

  private rowOf(member: TreeMember, companyOid: string): Outcome<CredentialRow> {
    const row = this.credentialRows(member).find(r => r.companyOid === companyOid);
    return row ? { value: row } : failure(404, 'NOT_FOUND', 'Compañía inexistente');
  }

  /** Whether the producer holding the licence may quote or issue `line` with `companyOid`. */
  availability(holderOid: string, companyOid: string, line: LineOfBusiness): { ok: boolean; reason: string | null } {
    const holder = this.db.members.find(m => m.oid === holderOid);
    if (holder && effectiveStatus(this.db.members, holder.oid).status !== 'active') {
      return { ok: false, reason: 'El productor titular de la matrícula está suspendido' };
    }
    const credential: CompanyCredential | undefined = this.db.credentials.find(c => c.memberOid === holderOid && c.companyOid === companyOid);
    if (!credential) return { ok: false, reason: 'Sin credenciales cargadas para esta compañía' };
    if (credential.validation === 'invalid') return { ok: false, reason: credential.validationMessage ?? 'Credenciales inválidas' };
    if (credential.validation === 'unvalidated') return { ok: false, reason: 'Credenciales sin validar' };
    if (!credential.enabledLines.includes(line)) return { ok: false, reason: 'Ramo no habilitado en las credenciales de esta compañía' };
    return { ok: true, reason: null };
  }
}
